import { QueryClient } from '@tanstack/react-query';
import { ApiError, isApiError } from './ApiError';

interface FullGetResult<T> {
  readonly body: T;
  readonly headers: Headers;
}

export class BaseApi {
  public readonly queryClient: QueryClient;

  protected constructor(queryClient?: QueryClient) {
    if (queryClient) {
      this.queryClient = queryClient;
    } else {
      this.queryClient = new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (isApiError(error) && error.status === 401) {
                return false;
              } else {
                return failureCount <= 3;
              }
            },
          },
        },
      });
    }
  }

  protected doDelete = async <T>(endpoint: string): Promise<T> => {
    const response = await fetch(endpoint, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const message = `Failed to DELETE ${endpoint}: ${response.status.toString()}`;
      console.error('%s: %o', message, response);
      throw new ApiError(response);
    }

    if (response.status === 204) {
      return undefined as T;
    } else {
      return (await response.json()) as T;
    }
  };

  protected doFullGet = async <T>(endpoint: string): Promise<FullGetResult<T>> => {
    const response = await fetch(endpoint);
    if (!response.ok) {
      const message = `Failed to GET ${endpoint}: ${response.status.toString()}`;
      console.error('%s: %o', message, response);
      throw new ApiError(response);
    }
    return {
      body: (await response.json()) as T,
      headers: response.headers,
    };
  };

  protected doGet = async <T>(endpoint: string): Promise<T> => (await this.doFullGet<T>(endpoint)).body;

  protected doPatch = async <T>(endpoint: string, eTag: string, body: BodyInit): Promise<T> => {
    const response = await fetch(endpoint, {
      method: 'PATCH',
      body,
      headers: {
        'Content-Type': 'application/json',
        'If-Match': eTag,
      },
    });

    if (!response.ok) {
      const message = `Failed to PATCH ${endpoint}: ${response.status.toString()}`;
      console.error('%s: %o', message, response);
      throw new ApiError(response);
    }

    if (response.status === 204) {
      return undefined as T;
    } else {
      return (await response.json()) as T;
    }
  };

  protected doPost = async <T>(endpoint: string, body: BodyInit): Promise<T> => {
    const response = await fetch(endpoint, {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const message = `Failed to POST ${endpoint}: ${response.status.toString()}`;
      console.error('%s: %o', message, response);
      throw new ApiError(response);
    }

    if (response.status === 204) {
      return undefined as T;
    } else {
      return (await response.json()) as T;
    }
  };
}
